import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast, Toaster } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Search, Plus, FileText, FileCode, Send, CheckCircle, XCircle,
  Stethoscope, AlertTriangle, Settings, Eye, EyeOff, ShieldCheck, RefreshCw,
  ListFilter,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_LABEL = {
  draft: 'Entwurf', validated: 'Validiert', sent: 'Gesendet',
  accepted: 'Angenommen', paid: 'Bezahlt', rejected: 'Zurückgewiesen',
  cancelled: 'Storniert', storno: 'Storno-Beleg',
};
const STATUS_COLOR = {
  draft: 'bg-zinc-200 text-zinc-800', validated: 'bg-blue-100 text-blue-800',
  sent: 'bg-amber-100 text-amber-800', accepted: 'bg-green-100 text-green-800',
  paid: 'bg-emerald-600 text-white', rejected: 'bg-red-100 text-red-800',
  cancelled: 'bg-zinc-300 text-zinc-600', storno: 'bg-purple-100 text-purple-800',
};

const KOGU_LABEL = {
  draft: 'KOGU-Entwurf', sent: 'Gesendet', accepted: 'Bewilligt',
  rejected: 'Abgelehnt', error: 'Transportfehler',
};
const KOGU_COLOR = {
  draft: 'bg-zinc-200 text-zinc-800', sent: 'bg-amber-100 text-amber-800',
  accepted: 'bg-green-100 text-green-800', rejected: 'bg-red-100 text-red-800',
  error: 'bg-red-100 text-red-800',
};
const KOGU_TYPE = { new: 'Neu', prolongation: 'Verlängerung', 're-consideration': 'Wiedererwägung' };

const KOGU_DECISION_LABEL = { accepted: 'Bewilligt', rejected: 'Abgelehnt', pending: 'In Prüfung' };
const KOGU_DECISION_COLOR = {
  accepted: 'bg-emerald-600 text-white', rejected: 'bg-red-600 text-white',
  pending: 'bg-blue-100 text-blue-800',
};
const KOGU_EXPIRY = {
  expiring_soon: { label: 'Läuft bald ab', cls: 'bg-amber-100 text-amber-800' },
  expired: { label: 'Abgelaufen', cls: 'bg-red-600 text-white' },
};

const PhysioBillingPage = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [patient, setPatient] = useState(null);
  const [billing, setBilling] = useState({});
  const [insurers, setInsurers] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [newRx, setNewRx] = useState({ physician_name: '', physician_gln: '', physician_zsr: '', diagnosis: '', sessions_granted: 9, series_no: 1 });
  const [docResults, setDocResults] = useState([]);
  const [docSearching, setDocSearching] = useState(false);
  const [docOpen, setDocOpen] = useState(false);
  const [sessionDlg, setSessionDlg] = useState(null); // prescription obj
  const [sessionLine, setSessionLine] = useState({ tariff_position_id: '', quantity: 1 });
  const [invoiceDlg, setInvoiceDlg] = useState(null); // prescription obj
  const [invForm, setInvForm] = useState({ insurer_id: '', tiers: 'payant', lines: [] });
  const [xsdDlg, setXsdDlg] = useState(null);
  const [costApprovals, setCostApprovals] = useState([]);
  const [koguDlg, setKoguDlg] = useState(null); // prescription obj
  const [koguForm, setKoguForm] = useState({ type: 'new', measures: '', remark: '', cost_estimate: true });
  const [koguResult, setKoguResult] = useState(null);
  const [koguBusy, setKoguBusy] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [expiringKogu, setExpiringKogu] = useState([]);
  const [overview, setOverview] = useState(null);     // null = zu, [] = offen/leer
  const [overviewFilter, setOverviewFilter] = useState('all');
  const [draftSessions, setDraftSessions] = useState({}); // pid -> number

  const loadDrafts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/physio-billing/prescription-drafts`, { headers: authHeaders() });
      setDrafts(res.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  const confirmDraft = async (d) => {
    try {
      const sessions = draftSessions[d.id] ?? d.sessions_granted ?? 9;
      await axios.post(`${API}/physio-billing/prescriptions/${d.id}/confirm`,
        { sessions_granted: sessions }, { headers: authHeaders() });
      toast.success(`Verordnung bestätigt · ${sessions} Sitzungen für Kunde freigegeben`);
      await loadDrafts();
      if (patient && patient.id === d.customer_id) await reloadPatient();
    } catch (e) { toast.error('Bestätigung fehlgeschlagen'); }
  };

  const loadStammdaten = useCallback(async () => {
    try {
      const [ins, tar] = await Promise.all([
        axios.get(`${API}/physio-billing/insurers`, { headers: authHeaders() }),
        axios.get(`${API}/physio-billing/tariff-positions`, { headers: authHeaders() }),
      ]);
      setInsurers(ins.data || []);
      setTariffs(tar.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { loadStammdaten(); }, [loadStammdaten]);
  useEffect(() => { loadDrafts(); }, [loadDrafts]);

  const loadExpiringKogu = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/physio-billing/cost-approvals-expiring`, { headers: authHeaders() });
      setExpiringKogu(Array.isArray(res.data) ? res.data : []);
    } catch (e) { /* still non-blocking */ }
  }, []);
  useEffect(() => { loadExpiringKogu(); }, [loadExpiringKogu]);

  const loadOverview = async () => {
    try {
      const res = await axios.get(`${API}/physio-billing/cost-approvals`, { headers: authHeaders() });
      setOverview(Array.isArray(res.data) ? res.data : []);
    } catch (e) { toast.error('Übersicht konnte nicht geladen werden'); setOverview([]); }
  };
  const toggleOverview = () => {
    if (overview === null) loadOverview(); else setOverview(null);
  };
  const koguBucket = (k) => {
    if (k.decision === 'accepted') return 'accepted';
    if (k.decision === 'rejected') return 'rejected';
    return 'open';
  };

  const search = async () => {
    if (!q.trim()) return;
    try {
      const res = await axios.get(`${API}/customers`, { params: { search: q.trim() }, headers: authHeaders() });
      setResults(Array.isArray(res.data) ? res.data : (res.data?.items || []));
    } catch (e) { toast.error('Suche fehlgeschlagen'); }
  };

  const selectPatient = async (c) => {
    setPatient(c);
    setResults([]);
    setQ('');
    try {
      const [b, rx, inv, kogu] = await Promise.all([
        axios.get(`${API}/physio-billing/patients/${c.id}/billing`, { headers: authHeaders() }),
        axios.get(`${API}/physio-billing/prescriptions`, { params: { customer_id: c.id }, headers: authHeaders() }),
        axios.get(`${API}/physio-billing/invoices`, { params: { customer_id: c.id }, headers: authHeaders() }),
        axios.get(`${API}/physio-billing/cost-approvals`, { params: { customer_id: c.id }, headers: authHeaders() }),
      ]);
      setBilling(b.data || {});
      setPrescriptions(rx.data || []);
      setInvoices(inv.data || []);
      setCostApprovals(kogu.data || []);
    } catch (e) { toast.error('Patientendaten konnten nicht geladen werden'); }
  };

  const reloadPatient = async () => { if (patient) await selectPatient(patient); };

  const saveBilling = async () => {
    try {
      await axios.put(`${API}/physio-billing/patients/${patient.id}/billing`, billing, { headers: authHeaders() });
      toast.success('Abrechnungsdaten gespeichert');
    } catch (e) { toast.error('Speichern fehlgeschlagen'); }
  };

  const createRx = async () => {
    try {
      await axios.post(`${API}/physio-billing/prescriptions`, { customer_id: patient.id, ...newRx }, { headers: authHeaders() });
      setNewRx({ physician_name: '', physician_gln: '', physician_zsr: '', diagnosis: '', sessions_granted: 9, series_no: 1 });
      setDocResults([]); setDocOpen(false);
      toast.success('Verordnung erfasst');
      reloadPatient();
    } catch (e) { toast.error('Verordnung fehlgeschlagen'); }
  };

  const searchDoctor = async () => {
    const term = (newRx.physician_name || '').trim();
    if (term.length < 2) { toast.error('Mind. 2 Zeichen eingeben'); return; }
    setDocSearching(true); setDocOpen(true);
    try {
      const r = await axios.get(`${API}/physio-billing/doctors/refdata`, { params: { q: term }, headers: authHeaders() });
      setDocResults(r.data?.results || []);
      if (!r.data?.configured) toast.error('Refdata nicht konfiguriert (Superadmin → Globale Integrationen)');
    } catch (e) { toast.error('Arztsuche fehlgeschlagen'); setDocResults([]); }
    finally { setDocSearching(false); }
  };

  const pickDoctor = (d) => {
    setNewRx({ ...newRx, physician_name: d.name || [d.first_name, d.last_name].filter(Boolean).join(' '), physician_gln: d.gln || '' });
    setDocOpen(false); setDocResults([]);
  };

  const toggleRelease = async (rx) => {
    try {
      await axios.post(`${API}/physio-billing/prescriptions/${rx.id}/release`,
        { released: !rx.released_to_customer }, { headers: authHeaders() });
      toast.success(!rx.released_to_customer ? 'Für Kunde freigegeben' : 'Freigabe entfernt');
      reloadPatient();
    } catch (e) { toast.error('Aktion fehlgeschlagen'); }
  };

  const recordSession = async () => {    try {
      await axios.post(`${API}/physio-billing/prescriptions/${sessionDlg.id}/sessions`,
        { lines: sessionLine.tariff_position_id ? [sessionLine] : [] }, { headers: authHeaders() });
      toast.success('Sitzung erfasst');
      setSessionDlg(null);
      setSessionLine({ tariff_position_id: '', quantity: 1 });
      reloadPatient();
    } catch (e) { toast.error('Sitzung konnte nicht erfasst werden'); }
  };

  const openInvoiceDlg = (rx) => {
    setInvoiceDlg(rx);
    setInvForm({ insurer_id: billing.insurer_id || (insurers[0]?.id || ''), tiers: 'payant', lines: [{ tariff_position_id: tariffs[0]?.id || '', quantity: 1 }] });
  };

  const addInvLine = () => setInvForm(f => ({ ...f, lines: [...f.lines, { tariff_position_id: tariffs[0]?.id || '', quantity: 1 }] }));
  const setInvLine = (i, patch) => setInvForm(f => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l) }));
  const rmInvLine = (i) => setInvForm(f => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const createInvoice = async () => {
    try {
      await axios.post(`${API}/physio-billing/invoices`, {
        customer_id: patient.id, prescription_id: invoiceDlg?.id || null,
        insurer_id: invForm.insurer_id, tiers: invForm.tiers,
        lines: invForm.lines.filter(l => l.tariff_position_id),
      }, { headers: authHeaders() });
      toast.success('Rechnung erstellt');
      setInvoiceDlg(null);
      reloadPatient();
    } catch (e) { toast.error(e.response?.data?.detail || 'Rechnung fehlgeschlagen'); }
  };

  const openKoguDlg = (rx) => {
    setKoguDlg(rx);
    setKoguResult(null);
    setKoguForm({
      type: 'new',
      measures: `${rx.sessions_granted || 9} Sitzungen Physiotherapie${rx.diagnosis ? ` bei ${rx.diagnosis}` : ''}`,
      remark: '',
      cost_estimate: true,
    });
  };

  const createKogu = async (send) => {
    if (!koguDlg) return;
    setKoguBusy(true);
    try {
      const res = await axios.post(`${API}/physio-billing/prescriptions/${koguDlg.id}/cost-approval`,
        { ...koguForm, send }, { headers: authHeaders() });
      setKoguResult(res.data || null);
      if (res.data?.xsd_valid === false) toast.error('XML weicht vom Schema ab – siehe Details.');
      else if (send) toast.success(res.data?.transport?.ok ? 'Kostengutsprache gesendet' : 'Gesendet (Transportfehler – siehe Details)');
      else toast.success('Kostengutsprache-Entwurf erstellt');
      const kogu = await axios.get(`${API}/physio-billing/cost-approvals`, { params: { customer_id: patient.id }, headers: authHeaders() });
      setCostApprovals(kogu.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kostengutsprache fehlgeschlagen');
    } finally { setKoguBusy(false); }
  };

  const sendKoguRecord = async (caId) => {
    try {
      const res = await axios.post(`${API}/physio-billing/cost-approvals/${caId}/send`, {}, { headers: authHeaders() });
      toast.success(res.data?.transport?.ok ? 'Kostengutsprache gesendet' : 'Gesendet (Transportfehler)');
      const kogu = await axios.get(`${API}/physio-billing/cost-approvals`, { params: { customer_id: patient.id }, headers: authHeaders() });
      setCostApprovals(kogu.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Senden fehlgeschlagen'); }
  };

  const recordKoguDecision = async (caId, decision) => {
    try {
      const body = { decision };
      if (decision === 'rejected') {
        const r = window.prompt('Begründung der Ablehnung (optional):', '');
        if (r) body.explanation = r;
      }
      await axios.post(`${API}/physio-billing/cost-approvals/${caId}/decision`, body, { headers: authHeaders() });
      toast.success(decision === 'accepted' ? 'Als bewilligt erfasst' : 'Als abgelehnt erfasst');
      reloadPatient();
    } catch (e) { toast.error(e.response?.data?.detail || 'Entscheid konnte nicht erfasst werden'); }
  };

  const syncKogu = async () => {
    try {
      const res = await axios.post(`${API}/physio-billing/cost-approvals/sync`, {}, { headers: authHeaders() });
      const d = res.data || {};
      if (d.matched > 0) toast.success(`${d.matched} Rückmeldung(en) verarbeitet`);
      else toast.info(d.message || 'Keine neuen Rückmeldungen');
      if (patient) reloadPatient();
      loadExpiringKogu();
    } catch (e) { toast.error(e.response?.data?.detail || 'Abruf fehlgeschlagen'); }
  };

  const reconsiderKogu = async (caId) => {
    try {
      await axios.post(`${API}/physio-billing/cost-approvals/${caId}/reconsider`, {}, { headers: authHeaders() });
      toast.success('Wiedererwägungs-Entwurf erstellt – bitte prüfen und senden');
      reloadPatient();
    } catch (e) { toast.error(e.response?.data?.detail || 'Wiedererwägung fehlgeschlagen'); }
  };

  const openKoguXml = (caId) => {
    const url = `${API}/physio-billing/cost-approvals/${caId}/xml`;
    axios.get(url, { headers: authHeaders() }).then(res => {
      const blob = new Blob([res.data], { type: 'application/xml' });
      window.open(URL.createObjectURL(blob), '_blank');
    }).catch(() => toast.error('XML konnte nicht geladen werden'));
  };

  const openKoguPdf = (caId) => {
    const url = `${API}/physio-billing/cost-approvals/${caId}/pdf`;
    axios.get(url, { headers: authHeaders(), responseType: 'blob' }).then(res => {
      const blob = new Blob([res.data], { type: 'application/pdf' });
      window.open(URL.createObjectURL(blob), '_blank');
    }).catch(() => toast.error('PDF konnte nicht erstellt werden'));
  };

  const openScan = (pid) => {
    axios.get(`${API}/physio-billing/prescriptions/${pid}/scan`, { headers: authHeaders(), responseType: 'blob' })
      .then(res => {
        const blob = new Blob([res.data], { type: res.data.type || 'application/pdf' });
        window.open(URL.createObjectURL(blob), '_blank');
      })
      .catch(() => toast.error('Verordnung konnte nicht geladen werden'));
  };

  const invAction = async (iid, action, body) => {
    try {
      const res = await axios.post(`${API}/physio-billing/invoices/${iid}/${action}`, body || {}, { headers: authHeaders() });
      if (action === 'validate') {
        const d = res.data || {};
        if (d.valid === false) toast.error('Pflichtfelder fehlen: ' + (d.errors || []).join(' '));
        else toast.success('Pflichtfelder OK – validiert');
        if (d.xsd_valid === false && (d.xsd_errors || []).length) {
          setXsdDlg({ errors: d.xsd_errors });
        } else if (d.xsd_valid) {
          toast.success('XSD-Schema (Forum 5.0): konform ✓');
        }
        reloadPatient();
        return;
      }
      toast.success(res.data?.note || 'Erledigt');
      reloadPatient();
    } catch (e) { toast.error(e.response?.data?.detail || 'Aktion fehlgeschlagen'); }
  };

  const openDoc = async (iid, kind) => {
    try {
      const res = await axios.get(`${API}/physio-billing/invoices/${iid}/${kind}`, {
        headers: authHeaders(), responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch (e) { toast.error('Dokument konnte nicht geöffnet werden'); }
  };

  const insurerName = (id) => insurers.find(i => i.id === id)?.name || '—';

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="physio-billing-page">
      <Toaster position="top-center" />
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Stethoscope className="h-7 w-7 text-emerald-600" />
          <div>
            <h1 className="text-2xl font-bold">Physio-Abrechnung</h1>
            <p className="text-sm text-muted-foreground">Verordnungen, Leistungen & Kassenrechnungen (Tiers payant/garant)</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={toggleOverview} data-testid="physio-kogu-overview-toggle">
            <ListFilter className="h-4 w-4 mr-2" /> KOGU-Übersicht
          </Button>
          <Button variant="outline" onClick={syncKogu} data-testid="physio-kogu-sync">
            <RefreshCw className="h-4 w-4 mr-2" /> Rückmeldungen abrufen
          </Button>
          <Button variant="outline" onClick={() => navigate('/settings/physio-billing')} data-testid="physio-open-settings">
            <Settings className="h-4 w-4 mr-2" /> Stammdaten
          </Button>
        </div>
      </div>

      {/* Dashboard-Hinweis: ablaufende Kostengutsprachen */}
      {expiringKogu.length > 0 && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="physio-kogu-expiry-banner">
          <div className="flex items-center gap-2 mb-2 text-amber-900 font-semibold">
            <AlertTriangle className="h-5 w-5" />
            {expiringKogu.length} Kostengutsprache{expiringKogu.length > 1 ? 'n' : ''} laufen bald ab oder sind abgelaufen
          </div>
          <ul className="space-y-1 text-sm text-amber-900">
            {expiringKogu.map(k => (
              <li key={k.id} className="flex items-center gap-2" data-testid={`physio-kogu-expiry-item-${k.id}`}>
                <Badge className={KOGU_EXPIRY[k.expiry_status]?.cls || 'bg-amber-100 text-amber-800'}>{KOGU_EXPIRY[k.expiry_status]?.label || k.expiry_status}</Badge>
                <span><strong>{k.patient_name}</strong> · gültig bis {new Date(k.valid_until).toLocaleDateString('de-CH')} · Ref. {k.request_id}</span>
                <button onClick={() => openKoguPdf(k.id)} className="inline-flex items-center gap-1 text-slate-700 ml-1"><FileText className="h-3 w-3" />PDF</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Patientenübergreifende KOGU-Übersicht */}
      {overview !== null && (
        <Card className="mb-6" data-testid="physio-kogu-overview-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ListFilter className="h-5 w-5 text-slate-600" />
              Alle Kostengutsprachen ({overview.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const counts = {
                all: overview.length,
                open: overview.filter(k => koguBucket(k) === 'open').length,
                accepted: overview.filter(k => k.decision === 'accepted').length,
                rejected: overview.filter(k => k.decision === 'rejected').length,
                expiring: overview.filter(k => ['expiring_soon', 'expired'].includes(k.expiry_status)).length,
              };
              const chips = [
                { key: 'all', label: 'Alle' },
                { key: 'open', label: 'Offen' },
                { key: 'accepted', label: 'Bewilligt' },
                { key: 'rejected', label: 'Abgelehnt' },
                { key: 'expiring', label: 'Läuft ab' },
              ];
              const filtered = overview.filter(k => {
                if (overviewFilter === 'all') return true;
                if (overviewFilter === 'expiring') return ['expiring_soon', 'expired'].includes(k.expiry_status);
                if (overviewFilter === 'open') return koguBucket(k) === 'open';
                return k.decision === overviewFilter;
              });
              return (
                <>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {chips.map(c => (
                      <button key={c.key} onClick={() => setOverviewFilter(c.key)}
                        data-testid={`physio-kogu-overview-filter-${c.key}`}
                        className={`px-3 py-1 rounded-full text-sm border transition-colors ${overviewFilter === c.key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}>
                        {c.label} <span className="opacity-70">({counts[c.key]})</span>
                      </button>
                    ))}
                  </div>
                  {filtered.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center" data-testid="physio-kogu-overview-empty">Keine Einträge in dieser Kategorie.</p>
                  ) : (
                    <div className="divide-y">
                      {filtered.map(k => (
                        <div key={k.id} className="flex items-center justify-between gap-3 py-2 flex-wrap" data-testid={`physio-kogu-overview-row-${k.id}`}>
                          <div className="text-sm">
                            <span className="font-semibold">{k.patient_name}</span>
                            <span className="text-muted-foreground"> · {KOGU_TYPE[k.type] || 'Neu'} · Ref. {k.request_id || '—'}</span>
                            {k.valid_until && <span className="text-muted-foreground"> · gültig bis {new Date(k.valid_until).toLocaleDateString('de-CH')}</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            {k.decision
                              ? <Badge className={KOGU_DECISION_COLOR[k.decision]}>{KOGU_DECISION_LABEL[k.decision]}</Badge>
                              : <Badge className={KOGU_COLOR[k.status]}>{KOGU_LABEL[k.status]}</Badge>}
                            {['expiring_soon', 'expired'].includes(k.expiry_status) && (
                              <Badge className={KOGU_EXPIRY[k.expiry_status]?.cls}>{KOGU_EXPIRY[k.expiry_status]?.label}</Badge>
                            )}
                            <button onClick={() => openKoguPdf(k.id)} className="inline-flex items-center gap-1 text-slate-700 text-sm" data-testid={`physio-kogu-overview-pdf-${k.id}`}>
                              <FileText className="h-4 w-4" />PDF
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {drafts.length > 0 && (
        <Card className="mb-6 border-emerald-300" data-testid="physio-drafts-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-5 w-5 text-emerald-600" />
              Kunden-Uploads · KI-erkannt ({drafts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {drafts.map(d => (
              <div key={d.id} className="border rounded-md p-3 bg-emerald-50/40" data-testid={`physio-draft-${d.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="text-sm space-y-1 min-w-0">
                    <div className="font-semibold">{d.customer_name || 'Kunde'}</div>
                    <div className="text-slate-600 truncate max-w-md" title={d.diagnosis || ''} data-testid={`physio-draft-diag-${d.id}`}>
                      {d.diagnosis || 'Diagnose unklar'}
                    </div>
                    <div className="text-muted-foreground">
                      Arzt: {d.physician_name || '—'}{d.physician_gln ? ` · GLN ${d.physician_gln}` : ''}{d.physician_zsr ? ` · ZSR ${d.physician_zsr}` : ''}
                    </div>
                    <div className="text-muted-foreground">
                      {d.therapy_type ? `${d.therapy_type} · ` : ''}{d.law || 'KVG'} · ausgestellt {d.issued_at || '—'}
                      {d.ai_extracted?.confidence != null && (
                        <span className="ml-2 text-xs">KI-Sicherheit {Math.round((d.ai_extracted.confidence || 0) * 100)}%</span>
                      )}
                    </div>
                    {d.scan_url && (
                      <button type="button" onClick={() => openScan(d.id)}
                        className="inline-flex items-center gap-1 text-emerald-700 underline text-xs" data-testid={`physio-draft-scan-${d.id}`}>
                        <Eye className="h-3 w-3" /> Verordnung ansehen
                      </button>
                    )}
                  </div>
                  <div className="flex items-end gap-2">
                    <div>
                      <Label className="text-xs">Sitzungen</Label>
                      <Input type="number" className="w-24 h-9"
                        value={draftSessions[d.id] ?? d.sessions_granted ?? 9}
                        onChange={e => setDraftSessions(s => ({ ...s, [d.id]: parseInt(e.target.value) || 0 }))}
                        data-testid={`physio-draft-sessions-${d.id}`} />
                    </div>
                    <Button size="sm" onClick={() => confirmDraft(d)} data-testid={`physio-draft-confirm-${d.id}`}>
                      <CheckCircle className="h-4 w-4 mr-1" /> Bestätigen & freigeben
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Patient search */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <Label>Patient suchen</Label>
          <div className="flex gap-2 mt-1">
            <Input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && search()}
              placeholder="Name, E-Mail oder Telefon" data-testid="physio-patient-search" />
            <Button onClick={search} data-testid="physio-patient-search-btn"><Search className="h-4 w-4" /></Button>
          </div>
          {results.length > 0 && (
            <div className="mt-2 border rounded-md divide-y max-h-56 overflow-auto">
              {results.map(c => (
                <button key={c.id} onClick={() => selectPatient(c)}
                  className="w-full text-left px-3 py-2 hover:bg-muted text-sm" data-testid={`physio-patient-result-${c.id}`}>
                  {(c.first_name || '') + ' ' + (c.last_name || '')} <span className="text-muted-foreground">{c.email}</span>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {!patient && (
        <p className="text-center text-muted-foreground py-10">Bitte zuerst einen Patienten auswählen.</p>
      )}

      {patient && (
        <>
          {/* Billing data */}
          <Card className="mb-6">
            <CardHeader><CardTitle className="text-base">{patient.first_name} {patient.last_name} — Abrechnungsdaten</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div><Label>AHV-Nr.</Label><Input value={billing.ahv_number || ''} onChange={e => setBilling({ ...billing, ahv_number: e.target.value })} placeholder="756...." data-testid="physio-ahv" /></div>
              <div><Label>Versichertennr.</Label><Input value={billing.insured_number || ''} onChange={e => setBilling({ ...billing, insured_number: e.target.value })} /></div>
              <div>
                <Label>Kostenträger</Label>
                <select className="w-full h-10 border rounded-md px-2 text-sm" value={billing.insurer_id || ''} onChange={e => setBilling({ ...billing, insurer_id: e.target.value })} data-testid="physio-insurer-select">
                  <option value="">– wählen –</option>
                  {insurers.map(i => <option key={i.id} value={i.id}>{i.name} ({i.law})</option>)}
                </select>
              </div>
              <div className="col-span-2 md:col-span-3">
                <Button size="sm" onClick={saveBilling} data-testid="physio-save-billing">Abrechnungsdaten speichern</Button>
              </div>
            </CardContent>
          </Card>

          {/* Prescriptions */}
          <Card className="mb-6">
            <CardHeader><CardTitle className="text-base">Verordnungen</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4 items-end">
                <div className="relative">
                  <Label>Arzt (Refdata-Suche)</Label>
                  <div className="flex gap-1">
                    <Input
                      value={newRx.physician_name}
                      onChange={e => setNewRx({ ...newRx, physician_name: e.target.value, physician_gln: '' })}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); searchDoctor(); } }}
                      placeholder="Name eingeben, z.B. Hatt"
                      data-testid="physio-rx-doc" />
                    <Button type="button" variant="outline" size="icon" onClick={searchDoctor} disabled={docSearching} data-testid="physio-rx-doc-search" title="In Refdata suchen">
                      <Search className="h-4 w-4" />
                    </Button>
                  </div>
                  {newRx.physician_gln && (
                    <div className="mt-1 text-xs text-emerald-700" data-testid="physio-rx-doc-gln">GLN {newRx.physician_gln} übernommen</div>
                  )}
                  {docOpen && (
                    <div className="absolute z-20 mt-1 w-[320px] max-h-72 overflow-auto rounded-lg border bg-white shadow-lg" data-testid="physio-rx-doc-results">
                      {docSearching && <div className="p-3 text-sm text-slate-500">Suche in Refdata…</div>}
                      {!docSearching && docResults.length === 0 && <div className="p-3 text-sm text-slate-500">Keine Treffer</div>}
                      {!docSearching && docResults.map((d, i) => (
                        <button type="button" key={d.gln || i} onClick={() => pickDoctor(d)} data-testid={`physio-rx-doc-result-${i}`}
                          className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50 border-b last:border-0">
                          <span className="font-medium text-slate-900">{d.name || `${d.first_name || ''} ${d.last_name || ''}`}</span>
                          <span className="block text-xs text-slate-500">{d.gln ? `GLN ${d.gln}` : ''}{(d.zip || d.city) ? ` · ${d.zip || ''} ${d.city || ''}` : ''}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div><Label>Diagnose</Label><Input value={newRx.diagnosis} onChange={e => setNewRx({ ...newRx, diagnosis: e.target.value })} data-testid="physio-rx-diag" /></div>
                <div><Label>Sitzungen</Label><Input type="number" value={newRx.sessions_granted} onChange={e => setNewRx({ ...newRx, sessions_granted: parseInt(e.target.value) || 9 })} /></div>
                <div><Label>Serie</Label><Input type="number" value={newRx.series_no} onChange={e => setNewRx({ ...newRx, series_no: parseInt(e.target.value) || 1 })} /></div>
                <Button onClick={createRx} data-testid="physio-rx-create"><Plus className="h-4 w-4 mr-1" />Verordnung</Button>
              </div>
              <div className="space-y-2">
                {prescriptions.map(rx => (
                  <div key={rx.id} className="border rounded-md p-3" data-testid={`physio-rx-${rx.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-sm">
                        <b>{rx.diagnosis || 'Verordnung'}</b> · {rx.physician_name || '—'} · Serie {rx.series_no}
                        {rx.released_to_customer && (
                          <Badge className="ml-2 bg-green-100 text-green-800" data-testid={`physio-rx-released-badge-${rx.id}`}>In Kunden-App sichtbar</Badge>
                        )}
                        <div className="text-muted-foreground">Sitzungen: {rx.sessions_used}/{rx.sessions_granted} · verbleibend {rx.sessions_remaining} · {rx.law}</div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant={rx.released_to_customer ? 'default' : 'outline'} onClick={() => toggleRelease(rx)} data-testid={`physio-rx-release-${rx.id}`}>
                          {rx.released_to_customer ? <><Eye className="h-4 w-4 mr-1" />Freigegeben</> : <><EyeOff className="h-4 w-4 mr-1" />Für Kunde freigeben</>}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setSessionDlg(rx)} data-testid={`physio-rx-session-${rx.id}`}>Sitzung erfassen</Button>
                        <Button size="sm" variant="outline" onClick={() => openKoguDlg(rx)} data-testid={`physio-rx-kogu-${rx.id}`}><ShieldCheck className="h-4 w-4 mr-1" />Kostengutsprache</Button>
                        <Button size="sm" onClick={() => openInvoiceDlg(rx)} data-testid={`physio-rx-invoice-${rx.id}`}>Rechnung</Button>
                      </div>
                    </div>
                    {costApprovals.filter(k => k.prescription_id === rx.id).map(k => (
                      <div key={k.id} className="mt-2 flex items-center gap-2 text-xs flex-wrap" data-testid={`physio-kogu-${k.id}`}>
                        <Badge className={KOGU_COLOR[k.status] || 'bg-zinc-200 text-zinc-800'}>{KOGU_LABEL[k.status] || k.status}</Badge>
                        {k.decision && <Badge className={KOGU_DECISION_COLOR[k.decision] || 'bg-zinc-200 text-zinc-800'} data-testid={`physio-kogu-decision-${k.id}`}>{KOGU_DECISION_LABEL[k.decision] || k.decision}</Badge>}
                        {KOGU_EXPIRY[k.expiry_status] && <Badge className={KOGU_EXPIRY[k.expiry_status].cls} data-testid={`physio-kogu-expiry-${k.id}`}><AlertTriangle className="h-3 w-3 mr-1" />{KOGU_EXPIRY[k.expiry_status].label}{k.valid_until ? ` (${new Date(k.valid_until).toLocaleDateString('de-CH')})` : ''}</Badge>}
                        <span className="text-muted-foreground">{k.request_id} · {KOGU_TYPE[k.type] || k.type} · CHF {Number(k.estimated_amount_chf || 0).toFixed(2)}</span>
                        {k.xsd_valid === true && <span className="text-green-700">XSD ✓</span>}
                        <button onClick={() => openKoguXml(k.id)} className="inline-flex items-center gap-1 text-blue-700" data-testid={`physio-kogu-xml-${k.id}`}><FileCode className="h-3 w-3" />XML</button>
                        <button onClick={() => openKoguPdf(k.id)} className="inline-flex items-center gap-1 text-slate-700" data-testid={`physio-kogu-pdf-${k.id}`}><FileText className="h-3 w-3" />PDF</button>
                        {k.status === 'draft' && <button onClick={() => sendKoguRecord(k.id)} className="inline-flex items-center gap-1 text-amber-700" data-testid={`physio-kogu-send-${k.id}`}><Send className="h-3 w-3" />Senden</button>}
                        {k.status === 'sent' && k.decision !== 'accepted' && k.decision !== 'rejected' && (
                          <>
                            <button onClick={() => recordKoguDecision(k.id, 'accepted')} className="inline-flex items-center gap-1 text-emerald-700" data-testid={`physio-kogu-accept-${k.id}`}><CheckCircle className="h-3 w-3" />Bewilligt</button>
                            <button onClick={() => recordKoguDecision(k.id, 'rejected')} className="inline-flex items-center gap-1 text-red-700" data-testid={`physio-kogu-reject-${k.id}`}><XCircle className="h-3 w-3" />Abgelehnt</button>
                          </>
                        )}
                        {k.decision === 'rejected' && (
                          <button onClick={() => reconsiderKogu(k.id)} className="inline-flex items-center gap-1 text-indigo-700 font-medium" data-testid={`physio-kogu-reconsider-${k.id}`}><RefreshCw className="h-3 w-3" />Wiedererwägung</button>
                        )}
                        {k.decision_explanation && <span className="text-muted-foreground italic">„{k.decision_explanation}"</span>}
                      </div>
                    ))}
                    {(rx.warnings || []).map((w, i) => (
                      <div key={i} className="mt-2 text-xs flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3 w-3" />{w}</div>
                    ))}
                  </div>
                ))}
                {prescriptions.length === 0 && <p className="text-sm text-muted-foreground">Noch keine Verordnungen.</p>}
              </div>
            </CardContent>
          </Card>

          {/* Invoices */}
          <Card>
            <CardHeader><CardTitle className="text-base">Kassenrechnungen</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {invoices.map(inv => (
                  <div key={inv.id} className="border rounded-md p-3 flex flex-wrap items-center gap-2 justify-between" data-testid={`physio-invoice-${inv.id}`}>
                    <div className="text-sm">
                      <b>{inv.invoice_no}</b> · {insurerName(inv.insurer_id)} · {inv.tiers} · CHF {Number(inv.total_chf).toFixed(2)}
                      <span className={`ml-2 px-2 py-0.5 rounded text-xs ${STATUS_COLOR[inv.status] || 'bg-zinc-200'}`}>{STATUS_LABEL[inv.status] || inv.status}</span>
                      {inv.xsd_valid === false && (
                        <button onClick={() => setXsdDlg({ errors: inv.xsd_errors || [] })}
                          className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800"
                          data-testid={`physio-inv-xsd-${inv.id}`}>
                          <AlertTriangle className="h-3 w-3" />XSD prüfen
                        </button>
                      )}
                      {inv.xsd_valid === true && (
                        <span className="ml-2 px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">XSD ✓</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openDoc(inv.id, 'xml')} title="XML" data-testid={`physio-inv-xml-${inv.id}`}><FileCode className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => openDoc(inv.id, 'pdf')} title="Rückforderungsbeleg (PDF)" data-testid={`physio-inv-pdf-${inv.id}`}><FileText className="h-4 w-4" /></Button>
                      {inv.status === 'draft' && <Button size="sm" variant="outline" onClick={() => invAction(inv.id, 'validate')} data-testid={`physio-inv-validate-${inv.id}`}><CheckCircle className="h-4 w-4 mr-1" />Validieren</Button>}
                      {inv.status === 'validated' && <Button size="sm" onClick={() => invAction(inv.id, 'send')} data-testid={`physio-inv-send-${inv.id}`}><Send className="h-4 w-4 mr-1" />Senden</Button>}
                      {(inv.status === 'sent' || inv.status === 'accepted') && <Button size="sm" variant="outline" onClick={() => invAction(inv.id, 'mark-paid')} data-testid={`physio-inv-paid-${inv.id}`}>Bezahlt</Button>}
                      {!['cancelled', 'storno', 'paid'].includes(inv.status) && <Button size="sm" variant="ghost" onClick={() => invAction(inv.id, 'storno', { reason: 'Korrektur' })} title="Storno" data-testid={`physio-inv-storno-${inv.id}`}><XCircle className="h-4 w-4" /></Button>}
                    </div>
                  </div>
                ))}
                {invoices.length === 0 && <p className="text-sm text-muted-foreground">Noch keine Rechnungen.</p>}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Session dialog */}
      <Dialog open={!!sessionDlg} onOpenChange={o => !o && setSessionDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Sitzung erfassen</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tarifposition (optional)</Label>
              <select className="w-full h-10 border rounded-md px-2 text-sm" value={sessionLine.tariff_position_id} onChange={e => setSessionLine({ ...sessionLine, tariff_position_id: e.target.value })} data-testid="physio-session-tariff">
                <option value="">– keine –</option>
                {tariffs.map(t => <option key={t.id} value={t.id}>{t.code} · {t.label} ({t.tax_points} TP)</option>)}
              </select>
            </div>
            <div><Label>Menge</Label><Input type="number" value={sessionLine.quantity} onChange={e => setSessionLine({ ...sessionLine, quantity: parseFloat(e.target.value) || 1 })} /></div>
          </div>
          <DialogFooter><Button onClick={recordSession} data-testid="physio-session-save">Sitzung buchen</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice dialog */}
      <Dialog open={!!invoiceDlg} onOpenChange={o => !o && setInvoiceDlg(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Kassenrechnung erstellen</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kostenträger</Label>
                <select className="w-full h-10 border rounded-md px-2 text-sm" value={invForm.insurer_id} onChange={e => setInvForm({ ...invForm, insurer_id: e.target.value })} data-testid="physio-inv-insurer">
                  {insurers.map(i => <option key={i.id} value={i.id}>{i.name} ({i.law})</option>)}
                </select>
              </div>
              <div>
                <Label>Zahlungsweg</Label>
                <select className="w-full h-10 border rounded-md px-2 text-sm" value={invForm.tiers} onChange={e => setInvForm({ ...invForm, tiers: e.target.value })} data-testid="physio-inv-tiers">
                  <option value="payant">Tiers payant (Kasse zahlt)</option>
                  <option value="garant">Tiers garant (Patient zahlt)</option>
                </select>
              </div>
            </div>
            <Label>Positionen</Label>
            {invForm.lines.map((l, i) => (
              <div key={i} className="flex gap-2 items-center">
                <select className="flex-1 h-9 border rounded-md px-2 text-sm" value={l.tariff_position_id} onChange={e => setInvLine(i, { tariff_position_id: e.target.value })} data-testid={`physio-inv-line-${i}`}>
                  {tariffs.map(t => <option key={t.id} value={t.id}>{t.code} · {t.label}</option>)}
                </select>
                <Input type="number" className="w-20" value={l.quantity} onChange={e => setInvLine(i, { quantity: parseFloat(e.target.value) || 1 })} />
                <Button size="sm" variant="ghost" onClick={() => rmInvLine(i)}><XCircle className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addInvLine}><Plus className="h-4 w-4 mr-1" />Position</Button>
          </div>
          <DialogFooter><Button onClick={createInvoice} data-testid="physio-inv-create">Rechnung erstellen</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Kostengutsprache dialog */}
      <Dialog open={!!koguDlg} onOpenChange={o => !o && setKoguDlg(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-600" />Kostengutsprache (eKOGU) erstellen</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Erzeugt eine elektronische Kostengutsprache nach Forum Datenaustausch generalCreditRequest 4.5 und sendet sie optional via MediData an den Kostenträger.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Art des Gesuchs</Label>
                <select className="w-full h-10 border rounded-md px-2 text-sm" value={koguForm.type} onChange={e => setKoguForm({ ...koguForm, type: e.target.value })} data-testid="physio-kogu-type">
                  <option value="new">Neu</option>
                  <option value="prolongation">Verlängerung</option>
                  <option value="re-consideration">Wiedererwägung</option>
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm" data-testid="physio-kogu-estimate">
                  <input type="checkbox" checked={koguForm.cost_estimate} onChange={e => setKoguForm({ ...koguForm, cost_estimate: e.target.checked })} />
                  Kostenschätzung
                </label>
              </div>
            </div>
            <div>
              <Label>Beantragte Massnahmen</Label>
              <textarea className="w-full border rounded-md px-2 py-1.5 text-sm min-h-[64px]" value={koguForm.measures} onChange={e => setKoguForm({ ...koguForm, measures: e.target.value })} data-testid="physio-kogu-measures" />
            </div>
            <div>
              <Label>Bemerkung (optional)</Label>
              <Input value={koguForm.remark} onChange={e => setKoguForm({ ...koguForm, remark: e.target.value })} data-testid="physio-kogu-remark" />
            </div>

            {koguResult && (
              <div className="rounded-md border p-3 text-sm space-y-1" data-testid="physio-kogu-result">
                <div className="flex items-center gap-2">
                  <Badge className={KOGU_COLOR[koguResult.status] || 'bg-zinc-200'}>{KOGU_LABEL[koguResult.status] || koguResult.status}</Badge>
                  <b>{koguResult.request_id}</b>
                  {koguResult.xsd_valid === true
                    ? <span className="text-green-700 text-xs">XSD ✓</span>
                    : <button onClick={() => setXsdDlg({ errors: koguResult.xsd_errors || [] })} className="text-amber-700 text-xs inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" />XSD prüfen</button>}
                </div>
                <div className="text-muted-foreground">Geschätzt: CHF {Number(koguResult.estimated_amount_chf || 0).toFixed(2)} · {koguResult.planned_sessions} Sitzungen · Kostenträger: {koguResult.insurer_name || '—'}</div>
                {(koguResult.warnings || []).map((w, i) => (
                  <div key={i} className="text-xs flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3 w-3" />{w}</div>
                ))}
                {koguResult.transport && (
                  <div className={`text-xs ${koguResult.transport.ok ? 'text-green-700' : 'text-red-700'}`}>Transport: {koguResult.transport.message || koguResult.transport.status}</div>
                )}
                <button onClick={() => openKoguXml(koguResult.id)} className="text-blue-700 text-xs inline-flex items-center gap-1" data-testid="physio-kogu-result-xml"><FileCode className="h-3 w-3" />XML anzeigen</button>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={koguBusy} onClick={() => createKogu(false)} data-testid="physio-kogu-draft">Entwurf erstellen</Button>
            <Button disabled={koguBusy} onClick={() => createKogu(true)} data-testid="physio-kogu-send"><Send className="h-4 w-4 mr-1" />Erstellen & senden</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* XSD errors dialog */}
      <Dialog open={!!xsdDlg} onOpenChange={o => !o && setXsdDlg(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" />Schema-Prüfung (Forum Datenaustausch 5.0)</DialogTitle></DialogHeader>
          <div className="text-sm space-y-2 max-h-96 overflow-auto">
            <p className="text-muted-foreground">Die Rechnung weicht noch vom offiziellen Schema ab. Bitte Daten ergänzen bzw. an die Technik weitergeben:</p>
            <ul className="list-disc pl-5 space-y-1">
              {(xsdDlg?.errors || []).map((e, i) => <li key={i} data-testid={`physio-xsd-error-${i}`}>{e}</li>)}
            </ul>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PhysioBillingPage;
